import { useCallback, useMemo } from 'react';
import { Alert, Button, Skeleton, Tag, Typography } from 'antd';

import RecordBadge from '../common/RecordBadge';
import RelatedListRow from './RelatedListRow';
import { buildCardHierarchyOptions } from './CardVisuals';
import { LOCK, PLUS } from '../icons';
import { normalizeId } from '../../utils/ids';
import { cardPagePath } from '../../utils/cards';
import {
	DEFAULT_LISTS_PER_CARD,
	RELATED_ITEMS_NAME,
	blankList,
	cardLine,
	limitRefusal,
	moveList,
	refusalForList,
	sortElementOf,
	sortOf,
	sourceCardsFor,
	sourceHasLink,
	withSort,
} from '../../utils/relatedLists';
import { orderLabel } from './RelatedListRow';

const { Text } = Typography;

/**
 * The card page's RELATED LISTS (epic KPE-029, KPR-720): named lists of the
 * items that link to the item being viewed, shown on the item view after its
 * first entry. A card's managers set them here, in the card's one draft and
 * with its one Save; the order they have here is the switcher's order.
 *
 *   Related items   fixed, first, not stored — every item shows it
 *   inherited       a parent card's lists, read-only, each naming its card
 *   own             this card's lists, editable (RelatedListRow)
 *
 * Only the card's own lists are written (`options.relatedLists`, at most
 * `limit`); the inherited ones are the API's computed copy. The card's
 * Save carries `ifUpdatedAt`, so a colleague's save in between is the page's
 * ordinary "Someone else changed this card" (409 `stale_card`).
 *
 * What the API refuses arrives as `saveError` (`key: 'relatedLists'`, the
 * list's position and the field) and is said under that field of that list.
 */

export default function RelatedListsOptions({
	cardId,
	parentCardId = null,
	lists = [],
	savedLists = [],
	inherited = [],
	// `undefined` is "still loading" (the card page's own convention): no default.
	collection,
	collectionId = null,
	cards = [],
	cardsById = null,
	getElementsForCard = null,
	limit = DEFAULT_LISTS_PER_CARD,
	saveError = null,
	disabled = false,
	onChange,
}) {
	const line = useMemo(() => cardLine(cardId, {
		...(cardsById || {}),
		[normalizeId(cardId)]: { _id: cardId, parentCardId },
	}), [cardId, parentCardId, cardsById]);

	// The pickers depend on which cards the lists name, not on what is typed in
	// their names: keyed on the ids so a keystroke recomputes nothing here.
	const sourceKey = lists.map(list => normalizeId(list.sourceCardId)).join('|');
	const sourceOptions = useMemo(() => {
		const keepIds = sourceKey.split('|').filter(Boolean);
		const sources = sourceCardsFor({ collection, cards, cardId, keepIds });
		return buildCardHierarchyOptions(sources);
	}, [collection, cards, cardId, sourceKey]);
	// Cards and their elements load apart from the card: until both are here
	// nothing is called unavailable.
	const ready = cards.length > 0 && Object.keys(cardsById || {}).length > 0;

	// A list with no lookup chosen reads any lookup on its source card that
	// points here; if there is none the API refuses it (it would be empty).
	const noLinkBySource = useMemo(() => {
		const out = {};
		sourceKey.split('|').filter(Boolean).forEach((source) => {
			out[source] = ready && !sourceHasLink({ sourceCardId: source, line, getElementsForCard });
		});
		return out;
	}, [sourceKey, line, getElementsForCard, ready]);

	const update = useCallback((next) => onChange(next), [onChange]);
	// An order cleared leaves no `sort` key at all (withSort): a list with no
	// order is stored without one, so setting and clearing it leaves the card clean.
	const patch = (index, change) => update(lists.map((list, i) => {
		if (i !== index) return list;
		const { sort, ...rest } = change;
		const next = { ...list, ...rest };
		return Object.prototype.hasOwnProperty.call(change, 'sort') ? withSort(next, sort) : next;
	}));

	const atLimit = lists.length >= limit;
	const tooMany = limitRefusal(saveError);
	const total = inherited.length + lists.length;
	const savedById = useMemo(() => new Map(savedLists.filter(l => l.id).map(l => [l.id, l])), [savedLists]);

	if (collection === undefined) {
		return (
			<section className='form-panel related-lists' data-testid='related-lists'>
				<div className='micro-label form-panel__kicker'>Related lists</div>
				<Skeleton active paragraph={{ rows: 2 }} title={false} />
			</section>
		);
	}

	return (
		<section className='form-panel related-lists' data-testid='related-lists'>
			<div className='micro-label form-panel__kicker'>
				Related lists
				<span className='form-panel__count'>{total + 1}</span>
			</div>
			<p className='form-panel__hint'>
				Named lists of the items that link to an item of this card, shown on its item view beside Related items.
				Their order here is the order they appear in. Every list shows its count under its default filter.
			</p>

			<ol className='related-lists__list' aria-label='Related lists'>
				<li className='related-list related-list--fixed' data-testid='related-list-fixed'>
					<span className='related-list__fixed-icon' aria-hidden='true'><LOCK /></span>
					<div className='related-list__fixed-text'>
						<strong>{RELATED_ITEMS_NAME}</strong>
						<Text type='secondary' className='d-block'>Everything that links to or from the item. Always first, and not editable.</Text>
					</div>
				</li>

				{inherited.map((entry) => {
					const owner = cardsById?.[normalizeId(entry.cardId)];
					const source = cardsById?.[normalizeId(entry.sourceCardId)];
					const order = sortOf(entry);
					const orderElement = order && typeof getElementsForCard === 'function'
						? sortElementOf(getElementsForCard(normalizeId(entry.sourceCardId)) || [], order.element)
						: null;
					return (
						<li key={`inherited-${entry.cardId}-${entry.id}`} className='related-list related-list--inherited' data-testid='related-list-inherited'>
							<div className='related-list__inherited-head'>
								<strong>{entry.name}</strong>
								{entry.hidden ? <Tag className='related-list__tag'>Hidden</Tag> : null}
							</div>
							<div className='related-list__inherited-meta'>
								<span>Shows items of</span>
								{source
									? <RecordBadge kind='card' record={source} title={source.name || '(untitled card)'} size='xs' />
									: <Text type='secondary'>a card you can’t open</Text>}
							</div>
							{orderElement ? (
								<div className='related-list__inherited-meta'>
									<span>In order of</span>
									<Text>{orderLabel(orderElement, order.direction)}</Text>
								</div>
							) : null}
							<div className='related-list__inherited-meta'>
								<span>Inherited from</span>
								{owner
									? <RecordBadge kind='card' record={owner} title={owner.name || '(untitled card)'} size='xs' to={cardPagePath(owner)} />
									: <Text type='secondary'>a parent card</Text>}
								<Text type='secondary'>Change it there.</Text>
							</div>
						</li>
					);
				})}

				{lists.map((list, index) => (
					<RelatedListRow
						key={list.id}
						list={list}
						index={index}
						total={lists.length}
						sourceOptions={sourceOptions}
						cardsById={cardsById}
						getElementsForCard={getElementsForCard}
						line={line}
						noLink={noLinkBySource[normalizeId(list.sourceCardId)] === true}
						collectionId={collectionId}
						cardId={cardId}
						ready={ready}
						saved={savedById.get(list.id) || null}
						refusal={refusalForList(saveError, index)}
						disabled={disabled}
						onChange={change => patch(index, change)}
						onMove={delta => update(moveList(lists, index, index + delta))}
						onRemove={() => update(lists.filter((_, i) => i !== index))}
					/>
				))}
			</ol>

			{!lists.length && !inherited.length ? (
				<p className='form-panel__hint related-lists__empty'>
					Add a list to show, say, this item’s open work items as their own table.
				</p>
			) : null}

			{tooMany ? (
				<Alert className='related-lists__alert' type='error' showIcon message={tooMany.message || `A card holds at most ${limit} related lists.`} />
			) : null}

			<div className='related-lists__add'>
				<Button
					type='dashed'
					icon={<PLUS />}
					disabled={disabled || atLimit}
					onClick={() => update([...lists, blankList()])}
				>
					Add a list
				</Button>
				{atLimit ? (
					<Text type='secondary' className='related-list__hint'>
						A card holds at most {limit} related lists. Remove one to add another.
					</Text>
				) : null}
			</div>
		</section>
	);
}
